import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Compass } from "lucide-react";
import { api } from "../lib/api";
import { Seo, breadcrumbLd } from "../lib/seo";
import { MaskedLines, Reveal, Chapter, ParallaxImg } from "../lib/motion";
import { BreakCard } from "../components/BreakCard";

export function HeartOfEngland() {
  const [dest, setDest] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api.get("/destinations/heart-of-england").then((r) => setDest(r.data)).catch(() => setMissing(true));
  }, []);

  if (missing) {
    return (
      <main className="container-tpb py-24 text-center">
        <h1 className="font-serif text-4xl">Page not found</h1>
        <Link to="/breaks" className="btn-primary mt-6">Browse all breaks</Link>
      </main>
    );
  }
  if (!dest) return <main className="container-tpb py-24"><p className="text-ink-muted">Loading…</p></main>;

  return (
    <main data-testid="heart-of-england-page">
      <Seo
        title={dest.seo_title}
        description={dest.seo_description}
        path="/heart-of-england"
        jsonLd={breadcrumbLd([{ name: "Home", path: "/" }, { name: "The Heart of England", path: "/heart-of-england" }])}
      />

      <section className="relative overflow-hidden bg-primary">
        <ParallaxImg src={dest.image} alt="Compton Verney in the Heart of England" className="absolute inset-0 h-full w-full" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/35 to-black/25" />
        <div className="container-tpb relative flex min-h-[56vh] flex-col justify-end pb-14 pt-32 sm:min-h-[64vh]">
          <p className="overline !text-sand/90 flex items-center gap-2" data-testid="destination-overline"><Compass className="h-4 w-4" /> Where we break</p>
          <h1 className="mt-4 max-w-4xl font-serif text-4xl font-medium leading-[1.03] tracking-tight text-sand sm:text-6xl" style={{ textShadow: "0 2px 22px rgba(0,0,0,0.45)" }} data-testid="destination-name">
            <MaskedLines immediate delay={0.3} lines={["The Heart of England"]} />
          </h1>
          <div className="mt-5 flex flex-wrap gap-2" data-testid="county-chips">
            {dest.counties?.map((c) => (
              <span key={c} className="rounded-full border border-sand/40 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-sand/90">{c}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="container-tpb py-16 sm:py-24">
        <div className="grid gap-14 lg:grid-cols-3">
          <Reveal className="lg:col-span-2">
            <p className="max-w-2xl text-lg leading-relaxed text-ink sm:text-xl" data-testid="destination-intro">{dest.intro}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <aside className="rounded-lg border border-sandline bg-white p-7" data-testid="destination-highlights">
              <h2 className="font-sans text-base font-semibold text-ink">While you're here</h2>
              <ul className="mt-4 space-y-2.5">
                {dest.highlights.map((h) => (
                  <li key={h} className="flex items-center gap-2.5 text-sm text-ink-muted">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" /> {h}
                  </li>
                ))}
              </ul>
            </aside>
          </Reveal>
        </div>

        <Chapter className="mt-20" num="№ 02" overline="The breaks" title={["Every Perfect Break,", "all in one place"]} />
        <div className="mt-14 grid gap-x-6 gap-y-14 sm:grid-cols-2" data-testid="destination-breaks-grid">
          {dest.breaks.map((b, i) => <BreakCard key={b.slug} brk={b} index={i} />)}
        </div>

        <Reveal>
          <div className="mt-20 rounded-lg bg-sand-dark p-8 text-center sm:p-12">
            <h2 className="mx-auto max-w-xl font-serif text-3xl font-medium text-primary">More of England coming soon</h2>
            <p className="mx-auto mt-3 max-w-md text-ink-muted">We're growing beyond the Heart of England. Join the list and be first to hear when new destinations land.</p>
            <Link to="/breaks" className="btn-accent group mt-7" data-testid="destination-cta">
              Browse all breaks <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
